/**
 * The Kola board inside the product app. Kola's embedded shell takes a
 * host: where its server is (the product API's /kola prefix, same origin),
 * how to prove who is calling (the product session's ID token), and the
 * theme to start on. Kola keeps its own router (a MemoryRouter); the
 * product URL follows it, and a product navigation pushes it a new path.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthSession } from './auth.js';
import { EmbeddedKolaApp, type KolaHost } from './kola/embedded.js';
import type { Theme } from './pages/Settings.js';

export const KOLA_API_BASE = '/kola';

/** Product URL for a Kola route: the dashboard is the product's Boards page, everything else keeps Kola's path. */
export function productPathForKola(kolaPath: string): string {
  const path = kolaPath.split('?')[0] ?? '/';
  return path === '/' || path === '' ? '/boards' : kolaPath;
}

export function KolaArea({ auth, theme, path, onPath }: { auth: AuthSession; theme: Theme; path: string; onPath: (productPath: string) => void }) {
  const host = useMemo<KolaHost>(() => ({
    kind: 'embedded',
    apiBase: KOLA_API_BASE,
    getIdToken: (forceFresh) => auth.idToken(forceFresh),
    openExternal: (url) => { window.open(url, '_blank', 'noopener,noreferrer'); },
    theme,
  }), [auth, theme]);
  // Each product-side change of the path is a new request to Kola's router; Kola's own moves come back through onPath.
  const [route, setRoute] = useState({ path, requestId: 0 });
  useEffect(() => { setRoute((r) => (r.path === path ? r : { path, requestId: r.requestId + 1 })); }, [path]);
  // Stable: Kola's route reporter re-fires whenever this callback changes.
  const onRouteChange = useCallback((p: string) => onPath(productPathForKola(p)), [onPath]);
  return <EmbeddedKolaApp host={host} route={route} onRouteChange={onRouteChange} />;
}
